/**
 * React Query hooks for Values
 *
 * Provides data fetching and mutation hooks for the Values system.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listValues,
  getValue,
  createValue,
  updateValue,
  deleteValue,
  getValuesStats,
  recordEvidence,
  getEvidence,
  createAutoTasks,
  getGuidance,
  seedValues,
  getSupportingTasks,
  removeSupportingTask,
} from '@/api/values';
import type {
  CreateValueInput,
  UpdateValueInput,
  RecordEvidenceInput,
} from '@/types/value';

// Query keys
export const valuesKeys = {
  all: ['values'] as const,
  lists: () => [...valuesKeys.all, 'list'] as const,
  list: (isActive?: boolean) => [...valuesKeys.lists(), { isActive }] as const,
  details: () => [...valuesKeys.all, 'detail'] as const,
  detail: (id: string) => [...valuesKeys.details(), id] as const,
  stats: () => [...valuesKeys.all, 'stats'] as const,
  evidence: (valueId: string) => [...valuesKeys.all, 'evidence', valueId] as const,
  guidance: (topic?: string) => [...valuesKeys.all, 'guidance', topic] as const,
  supportingTasks: (valueId: string) => [...valuesKeys.all, 'supportingTasks', valueId] as const,
};

/**
 * Hook to list values
 */
export function useValues(isActive?: boolean) {
  return useQuery({
    queryKey: valuesKeys.list(isActive),
    queryFn: () => listValues(isActive),
  });
}

/**
 * Hook to get a single value
 */
export function useValue(valueId: string) {
  return useQuery({
    queryKey: valuesKeys.detail(valueId),
    queryFn: () => getValue(valueId),
    enabled: !!valueId,
  });
}

/**
 * Hook to get values statistics
 */
export function useValuesStats() {
  return useQuery({
    queryKey: valuesKeys.stats(),
    queryFn: () => getValuesStats(),
  });
}

/**
 * Hook to get evidence for a value
 */
export function useValueEvidence(valueId: string, limit: number = 30) {
  return useQuery({
    queryKey: valuesKeys.evidence(valueId),
    queryFn: () => getEvidence(valueId, limit),
    enabled: !!valueId,
  });
}

/**
 * Hook to get guidance content
 */
export function useGuidance(topic?: string) {
  return useQuery({
    queryKey: valuesKeys.guidance(topic),
    queryFn: () => getGuidance(topic),
  });
}

/**
 * Hook to create a value
 */
export function useCreateValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateValueInput) => createValue(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: valuesKeys.all });
    },
  });
}

/**
 * Hook to update a value
 */
export function useUpdateValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ valueId, input }: { valueId: string; input: UpdateValueInput }) =>
      updateValue(valueId, input),
    onSuccess: (_, { valueId }) => {
      queryClient.invalidateQueries({ queryKey: valuesKeys.all });
      queryClient.invalidateQueries({ queryKey: valuesKeys.detail(valueId) });
    },
  });
}

/**
 * Hook to delete a value
 */
export function useDeleteValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (valueId: string) => deleteValue(valueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: valuesKeys.all });
    },
  });
}

/**
 * Hook to record evidence
 */
export function useRecordEvidence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ valueId, input }: { valueId: string; input: RecordEvidenceInput }) =>
      recordEvidence(valueId, input),
    onSuccess: (_, { valueId }) => {
      queryClient.invalidateQueries({ queryKey: valuesKeys.all });
      queryClient.invalidateQueries({ queryKey: valuesKeys.evidence(valueId) });
    },
  });
}

/**
 * Hook to create auto-tasks for neglected values
 */
export function useCreateAutoTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => createAutoTasks(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: valuesKeys.all });
    },
  });
}

/**
 * Hook to seed values (development)
 */
export function useSeedValues() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => seedValues(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: valuesKeys.all });
    },
  });
}

// =============================================================================
// SUPPORTING TASKS (Task-Value Edges)
// =============================================================================

/**
 * Hook to get tasks that support (serve) a value
 */
export function useSupportingTasks(valueId: string) {
  return useQuery({
    queryKey: valuesKeys.supportingTasks(valueId),
    queryFn: () => getSupportingTasks(valueId),
    enabled: !!valueId,
  });
}

/**
 * Hook to remove a task-value association
 */
export function useRemoveSupportingTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ valueId, taskId }: { valueId: string; taskId: string }) =>
      removeSupportingTask(valueId, taskId),
    onSuccess: (_, { valueId }) => {
      // Invalidate supporting tasks for this value
      queryClient.invalidateQueries({ queryKey: valuesKeys.supportingTasks(valueId) });
      // Also invalidate task values queries
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

